/**
 * Calendar Week View
 * Display appointments in a weekly timeline (Mon-Fri, 8:00-17:00)
 */

import type { AppointmentWithDetails } from '../../hooks/useAppointments';
import {
  getWeekDays,
  formatDateShort,
  formatTime,
  getStatusColor,
  isToday,
  getIcelandicWeekdayShort,
  timeToMinutes,
  getTimeFromISO,
  getDateFromISO,
  isSameDay,
  parseISODateTime,
} from '../../utils/calendar';

interface CalendarWeekViewProps {
  weekStart: Date;
  appointments: AppointmentWithDetails[];
  onAppointmentClick: (appointment: AppointmentWithDetails) => void;
  onTimeSlotClick: (date: string, time: string) => void;
}

export function CalendarWeekView({
  weekStart,
  appointments,
  onAppointmentClick,
  onTimeSlotClick,
}: CalendarWeekViewProps) {
  const weekDays = getWeekDays(weekStart);

  // Generate time slots (8:00-17:00, 15-minute intervals)
  const timeSlots: string[] = [];
  for (let hour = 8; hour < 17; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      timeSlots.push(`${h}:${m}`);
    }
  }

  // Get appointments for a specific day and time slot
  const getAppointmentsForSlot = (day: Date, time: string) => {
    return appointments.filter((apt) => {
      const aptDate = parseISODateTime(apt.appointment_datetime);
      const aptTime = getTimeFromISO(apt.appointment_datetime);
      const aptTimeMinutes = timeToMinutes(aptTime);
      const slotTimeMinutes = timeToMinutes(time);

      // Check if appointment starts at or overlaps this slot
      return (
        isSameDay(aptDate, day) &&
        aptTimeMinutes <= slotTimeMinutes &&
        aptTimeMinutes + apt.duration_minutes > slotTimeMinutes
      );
    });
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow overflow-hidden">
      {/* Header with days */}
      <div className="grid grid-cols-[60px_repeat(5,1fr)] border-b border-gray-200 bg-gray-50">
        <div className="p-2"></div>
        {weekDays.map((day) => (
          <div
            key={day.toISOString()}
            className={`p-3 text-center border-l border-gray-200 ${
              isToday(day) ? 'bg-blue-50' : ''
            }`}
          >
            <div className="text-xs font-medium text-gray-500">
              {getIcelandicWeekdayShort(day.getDay())}
            </div>
            <div
              className={`text-lg font-semibold ${isToday(day) ? 'text-blue-600' : 'text-gray-900'}`}
            >
              {day.getDate()}
            </div>
            <div className="text-xs text-gray-500">{formatDateShort(day)}</div>
          </div>
        ))}
      </div>

      {/* Time grid */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-[60px_repeat(5,1fr)]">
          {/* Time column */}
          <div className="border-r border-gray-200">
            {timeSlots.map((time) => {
              const isHourMark = time.endsWith(':00');
              return (
                <div
                  key={time}
                  className={`px-2 py-1 text-right text-xs text-gray-500 ${
                    isHourMark ? 'font-medium' : 'text-gray-400'
                  }`}
                  style={{ height: '60px' }}
                >
                  {isHourMark ? time : ''}
                </div>
              );
            })}
          </div>

          {/* Day columns */}
          {weekDays.map((day) => (
            <div key={day.toISOString()} className="border-l border-gray-200 relative">
              {timeSlots.map((time) => {
                const slotAppointments = getAppointmentsForSlot(day, time);
                const isHourMark = time.endsWith(':00');
                const dateStr = getDateFromISO(day.toISOString());

                return (
                  <div
                    key={time}
                    className={`border-t ${isHourMark ? 'border-gray-300' : 'border-gray-100'} hover:bg-blue-50 cursor-pointer transition-colors relative`}
                    style={{ height: '60px' }}
                    onClick={() => {
                      if (slotAppointments.length === 0) {
                        onTimeSlotClick(dateStr, time);
                      }
                    }}
                  >
                    {slotAppointments.map((apt) => {
                      const aptTime = getTimeFromISO(apt.appointment_datetime);
                      const isFirstSlot = aptTime === time;

                      if (!isFirstSlot) return null;

                      const statusColors = getStatusColor(apt.status);
                      const durationSlots = Math.ceil(apt.duration_minutes / 15);
                      const height = durationSlots * 60;

                      return (
                        <div
                          key={apt.id}
                          className={`absolute inset-x-1 border-l-4 ${statusColors.border} ${statusColors.bg} rounded p-1 overflow-hidden cursor-pointer ${statusColors.hover} z-10`}
                          style={{
                            height: `${height - 8}px`,
                            top: '4px',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAppointmentClick(apt);
                          }}
                        >
                          <div className="text-xs font-semibold truncate">
                            {apt.patient_name}
                          </div>
                          <div className="text-xs truncate">{apt.appointment_type}</div>
                          <div className="text-xs text-gray-600">{formatTime(apt.appointment_datetime)}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
